import React, { Fragment, useState } from 'react';
import 'firebase/firestore';
import 'firebase/storage';
import Icon from '../shared/icon';

export const OUTLINER_MODE_LAYERS = 'entities';
export const OUTLINER_MODE_LIBRARY = 'library';
export const OUTLINER_MODE_ENVIRONMENT = 'settings';

function Caret({ open, onClick }) {
  let pathData;
  if (open) {
    pathData = 'M3 5l3-4H0l3 4z';
  } else {
    pathData = 'M5 3L1 0v6l4-3z';
  }
  return (
    <div className="outliner__caret" onClick={() => onClick && onClick()}>
      <svg width="6" height="6" viewBox="0 0 6 6">
        <path d={pathData} fill="#c4c4c4" />
      </svg>
    </div>
  );
}

function PanelTab({ name, label, outlinerMode, onChangeOutlinerMode }) {
  return (
    <div className={`panel__tab${outlinerMode === name ? ' active' : ''}`} onClick={() => onChangeOutlinerMode(name)}>
      {label}
    </div>
  );
}

function LayersPanel({
  world,
  activeLayer,
  selectedEntities,
  onSelectEntity,
  onToggleLayerVisibility,
  onToggleLayerExpanded,
  onSetActiveLayer,
  onCreateLayer,
}) {
  const _onCreateLayer = () => {
    const layerName = prompt('Layer name');
    if (layerName && layerName.length > 0) {
      onCreateLayer(layerName);
    }
  };
  return (
    <div className="layers">
      <div className="outliner__row outliner__header">
        <div className="outliner__text"></div>
        <div className="outliner__actions">
          <Icon name="plus" onClick={_onCreateLayer} />
        </div>
      </div>

      {world.layers.map((layer) => (
        <Layer
          key={layer.name}
          layer={layer}
          activeLayer={activeLayer}
          selectedEntities={selectedEntities}
          onSelectEntity={onSelectEntity}
          onToggleLayerVisibility={onToggleLayerVisibility}
          onToggleLayerExpanded={onToggleLayerExpanded}
          onSetActiveLayer={onSetActiveLayer}
        />
      ))}
    </div>
  );
}

function Entity({ layer, entity, selected, onSelectEntity }) {
  return (
    <div
      className={`outliner__row cursor-pointer outliner__entity ${selected ? ' outliner__row--highlight' : ''} ${
        layer.editorVisible ? '' : 'outliner__disabled'
      }`}
      onClick={() => onSelectEntity(entity)}
    >
      <div className="outliner__indent-2" />
      <div className="outliner__icon">
        <Icon name={entity.kind} />
      </div>
      <div className="outliner__text">
        <span className="entity__id">{entity.id}</span>
        <span className="entity__kind">{entity.kind}</span>
      </div>
      <div className="outliner__actions" />
    </div>
  );
}

function Layer({ layer, activeLayer, selectedEntities, onSelectEntity, onToggleLayerVisibility, onToggleLayerExpanded, onSetActiveLayer }) {
  return (
    <Fragment>
      <div
        className={`outliner__row ${layer === activeLayer ? 'outliner__row--active' : ''} ${
          layer.editorVisible ? '' : 'outliner__disabled'
        }`}
      >
        <div className="outliner__indent-0">
          <Caret open={layer.editorExpanded} onClick={() => onToggleLayerExpanded(layer)} />
        </div>
        <div className="outliner__icon cursor-pointer" onClick={() => onSetActiveLayer(layer)}>
          <Icon name={layer === activeLayer ? 'layerActive' : 'layerInactive'} />
        </div>
        <div className="outliner__text">{layer.name}</div>
        <div className="outliner__actions">
          <Icon name={layer.editorVisible ? 'eyeOpen' : 'eyeClosed'} onClick={() => onToggleLayerVisibility(layer)} />
        </div>
      </div>

      {layer.editorExpanded &&
        layer.entities.map((entity) => (
          <Entity key={entity.id} layer={layer} entity={entity} selected={selectedEntities.has(entity)} onSelectEntity={onSelectEntity} />
        ))}
    </Fragment>
  );
}

function LibraryPanel({ world, assetLibrary, selectedAsset, onSelectAsset, onCreateAssetGhost }) {
  const [search, setSearch] = useState('');
  if (!assetLibrary) {
    return <div className="library">Loading...</div>;
  }

  let filteredAssets = assetLibrary.assets.filter((asset) => !asset.name.includes('@'));
  filteredAssets = filteredAssets.filter((asset) => asset.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="library">
      <div className="outliner__row outliner__header">
        <div className="outliner__indent-2 outliner__text">
          {assetLibrary.name} [{assetLibrary.filename}]
        </div>
      </div>
      <div className="outliner__row outliner__header">
        <div className="outliner__indent-2 outliner__search">
          <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {filteredAssets.map((asset) => {
        let assetCount = 0;
        let assetId = world.findAssetIdByName(asset.name);
        // console.log(asset, assetId);
        if (assetId !== null) {
          for (const layer of world.layers) {
            for (const entity of layer.entities) {
              if (entity.assetIds.includes(assetId)) {
                assetCount++;
              }
            }
          }
        }

        return (
          <div
            key={asset.name}
            className={`outliner__row ${asset === selectedAsset ? 'outliner__row--highlight' : ''}`}
            onMouseDown={(e) => onCreateAssetGhost(e, asset)}
            onClick={() => onSelectAsset(asset)}
          >
            <div className="outliner__icon outliner__indent-1">
              <Icon name={asset.kind} />
            </div>
            <div className="outliner__text">
              <span className="asset__name">{asset.name}</span>
            </div>
            <div className="outliner__actions" title="How many times this asset is used.">
              {assetCount}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Range({ environment, name, label, onChangeEnvironment }) {
  return (
    <div className="outliner__row environment__row">
      <label className="environment__label">{label}</label>
      <input
        className="environment__range"
        type="range"
        min="0"
        max="100"
        value={environment[name]}
        onChange={(e) => onChangeEnvironment(name, parseInt(e.currentTarget.value))}
      />
      <span className="environment__value">{environment[name]}</span>
    </div>
  );
}

function EnvironmentPanel({ environment, onChangeEnvironment }) {
  return (
    <div className="outliner__panel settings">
      <Range environment={environment} name="pedestrians" label="Pedestrians" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="bikes" label="Bikes" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="cars" label="Cars" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="trucks" label="Trucks" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="buses" label="Buses" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="passengerTrains" label="Passenger Trains" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="freightTrains" label="Freight Trains" onChangeEnvironment={onChangeEnvironment} />
      <Range environment={environment} name="boats" label="Boats" onChangeEnvironment={onChangeEnvironment} />
    </div>
  );
}

export default function Outliner({
  world,
  environment,
  assetLibrary,
  selection,
  selectedAsset,
  activeLayer,
  onSelectEntity,
  outlinerMode,
  onChangeOutlinerMode,
  onChangeEnvironment,
  onSelectAsset,
  onCreateAssetGhost,
  onToggleLayerVisibility,
  onToggleLayerExpanded,
  onSetActiveLayer,
  onCreateLayer,
}) {
  if (!world) {
    return <div className="outliner"></div>;
  }
  // Selection is expressed as cells.
  // Find all entities that have these cells.
  const selectedEntities = activeLayer ? activeLayer.findEntitiesForCells(selection) : new Set();

  return (
    <div className="outliner">
      <div className="panel__tabs">
        <PanelTab name={OUTLINER_MODE_LAYERS} label="Layers" outlinerMode={outlinerMode} onChangeOutlinerMode={onChangeOutlinerMode} />
        <PanelTab name={OUTLINER_MODE_LIBRARY} label="Library" outlinerMode={outlinerMode} onChangeOutlinerMode={onChangeOutlinerMode} />
        <PanelTab
          name={OUTLINER_MODE_ENVIRONMENT}
          label="Environment"
          outlinerMode={outlinerMode}
          onChangeOutlinerMode={onChangeOutlinerMode}
        />
      </div>
      <div className="panel__content">
        {world && outlinerMode === OUTLINER_MODE_LAYERS && (
          <LayersPanel
            world={world}
            activeLayer={activeLayer}
            selectedEntities={selectedEntities}
            onSelectEntity={onSelectEntity}
            onToggleLayerVisibility={onToggleLayerVisibility}
            onToggleLayerExpanded={onToggleLayerExpanded}
            onSetActiveLayer={onSetActiveLayer}
            onCreateLayer={onCreateLayer}
          />
        )}
        {world && assetLibrary && outlinerMode === OUTLINER_MODE_LIBRARY && (
          <LibraryPanel
            world={world}
            assetLibrary={assetLibrary}
            selectedAsset={selectedAsset}
            onSelectAsset={onSelectAsset}
            onCreateAssetGhost={onCreateAssetGhost}
          />
        )}
        {world && outlinerMode === OUTLINER_MODE_ENVIRONMENT && (
          <EnvironmentPanel environment={environment} onChangeEnvironment={onChangeEnvironment} />
        )}
      </div>
    </div>
  );
}
